import React, { useEffect } from 'react';
import { Alert, Stack } from 'react-bootstrap';
import {
  FormWrapper,
  LoadingAnimation,
  Navigation,
  useWizardContext,
} from '@odmonorepo/core-components';
import { getCheckoutState, getIdentityChallengeQuestions, postIdentityChallengeAnswers } from 'merchant-api/checkout';
import { FormattedMessage } from 'react-intl';
import { IdentityForm } from './IdentityForm';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { IdentityQuestion } from 'merchant-api/types';
import { toIdentityAnswers, getNextStepAfterIdentity } from './helper';

export const IdentityChallenge = () => {
  const { goToStep } = useWizardContext();
  const { checkoutData, isErrorCheckoutState, isLoadingCheckoutState } = getCheckoutState();
  const { identityChallengeQuestionsData, isLoadingIdentityChallengeQuestions, isErrorIdentityChallengeQuestions } = getIdentityChallengeQuestions();
  const buildIdentitySchema = (questions: IdentityQuestion[]) =>
    yup.object(
      Object.fromEntries(
        questions.map((q) => [q.type.replace(/\./g, '_'), yup.string().required('Select an answer.')])
      )
    );
  const schema = buildIdentitySchema(identityChallengeQuestionsData?.questions ?? []);

  if (isErrorIdentityChallengeQuestions && checkoutData?.identityVerificationStatus === 'CHALLENGE') goToStep('IDENTITY_FAILED');
  if (isErrorCheckoutState) throw new Error('Error loading checkout state');

  if (isLoadingIdentityChallengeQuestions || isLoadingCheckoutState || !identityChallengeQuestionsData) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  return (
    <FormWrapper schema={schema}>
      <IdentityChallengeForm identityChallengeQuestionsData={identityChallengeQuestionsData} checkoutData={checkoutData} />
    </FormWrapper>
  )

};

const IdentityChallengeForm = ({identityChallengeQuestionsData, checkoutData}) => {
  const { sendIdentityChallengeAnswers, isLoadingIdentityChallengeAnswers } = postIdentityChallengeAnswers();
  const { stepOrder, goToStep } = useWizardContext();
  const { getValues } = useFormContext();

  const onSubmit = () => {
    sendIdentityChallengeAnswers(toIdentityAnswers(getValues(), identityChallengeQuestionsData.idNumber))
      .then(() => {
        const nextStep = getNextStepAfterIdentity({
          stepOrder,
          checkoutData,
        });

        goToStep(nextStep);
      })
      .catch(() => {
        goToStep('IDENTITY_FAILED');
      });
  };

   useEffect(() => {
    if (checkoutData?.identityVerificationStatus === 'FAIL' ) {
      goToStep('IDENTITY_FAILED');
    }
  }, [checkoutData]);

  if( isLoadingIdentityChallengeAnswers ) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  return(
    <Stack gap={4} style={{ maxWidth: '600px', width: '100%' }} className="mx-auto">
      <Alert variant="warning">
        <strong className='fs-6'><FormattedMessage id="identity.challengeAlert"/></strong><br />
        <FormattedMessage id="identity.challengeAlertSubtitle" />
      </Alert>
      <h3 className="text-center">
        <FormattedMessage id="identity.title" />
      </h3>
      <IdentityForm identityData={ identityChallengeQuestionsData }/>
      <Navigation continueOnly handleNext={onSubmit} continueText={stepOrder.includes('REQUIRED_FIRST_DRAW') ? "Continue to First Draw" : "Continue to Agreement"}/>
    </Stack>
  );
}

export default IdentityChallenge;
